import { isEmpty } from 'lodash';

import { DwhSriCategory } from '@ndvr/market-data-api';
import { RestrictionType, TradeRestrictionCategoryOverride } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { logger } from 'utils';
import { useGetPortfolioTradePreferences } from 'views/portfolio/account-group-queries';
import { useSriData } from 'views/portfolio/PortfolioPage/utils/useSriData';

export interface RestrictionsInfo {
    isLoading: boolean;
    clientRestrictions: { shareId: string; ticker: string; company: string; restrictionType: RestrictionType }[];
    categories?: DwhSriCategory[];
    overrides?: TradeRestrictionCategoryOverride[];
    sriRestrictions?: {
        [categoryId: string]: {
            categoryName?: string;
            categoryId: string;
            subCategoryName?: string;
            subCategoryId: string;
            exceptions: { ticker: string; shareId: string; shareClassId?: string; company: string }[];
        }[];
    };
}

export const useRestrictionsData = (accountGroupGUID: string, stocksMap: Map<string, AssetInfo>) => {
    const { categories, isLoading: isLoadingCategories, isFetching: isFetchingCategories } = useSriData();
    const {
        data: preferences,
        isLoading: isLoadingPreferences,
        isFetching: isFetchingPreferences,
    } = useGetPortfolioTradePreferences(accountGroupGUID);

    const sriRestrictions: RestrictionsInfo['sriRestrictions'] = {};
    if (!isEmpty(categories) && !isEmpty(preferences)) {
        categories?.forEach((category) => {
            category.subCategories.forEach((subCategory) => {
                const exceptions = preferences?.restrictedCategoryOverrides
                    ?.filter((override) => subCategory.scIds.includes(override.shareClassId || 'x'))
                    .map((stock) => ({
                        ...stock,
                        ticker: stocksMap.get(stock.shareId)?.symbol || '',
                        company: stocksMap.get(stock.shareId)?.description || '',
                    }));

                if (preferences?.restrictedCategoryIds?.includes(subCategory.sriSubCategoryId)) {
                    sriRestrictions[category.sriCategoryId] = sriRestrictions[category.sriCategoryId] || [];
                    sriRestrictions[category.sriCategoryId].push({
                        categoryName: category.sriCategoryLabel,
                        categoryId: category.sriCategoryId,
                        subCategoryName: subCategory.sriSubCategoryLabel,
                        subCategoryId: subCategory.sriSubCategoryId,
                        exceptions,
                    });
                }
            });
        });
    }

    const restrictionsInfo: RestrictionsInfo = {
        isLoading: isLoadingPreferences || isFetchingPreferences || isLoadingCategories || isFetchingCategories,
        categories,
        overrides: preferences?.restrictedCategoryOverrides,
        clientRestrictions:
            preferences?.restrictions?.map((clientRestriciton) => ({
                shareId: clientRestriciton.shareId,
                ticker: stocksMap.get(clientRestriciton.shareId)?.symbol || '',
                company: stocksMap.get(clientRestriciton.shareId)?.description || '',
                restrictionType: clientRestriciton.restrictionType,
            })) || [],
        sriRestrictions,
    };

    return {
        preferences,
        restrictionsInfo: restrictionsInfo,
    };
};
